import React, { Component } from 'react'
import { Container, Row, Col, Button } from 'react-bootstrap'
import { connect } from "react-redux";
import MapBrowser from './map';
import Table from 'react-bootstrap/Table'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, ReferenceDot } from 'recharts'
import Dropdown from 'react-dropdown';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { playerSelector } from "../../actions";
import { wsHOST } from '../../Config';
import { calc_distance, convertGpsTime } from '../helpers';
import './main.css';

const VERSION = 'v2';
const wsUrl = `${wsHOST}/${VERSION}/`;

// 相補フィルター + EMAスムージングによる姿勢推定
// ジャイロの短期安定性と加速度の長期安定性を融合し、
// EMAローパスフィルタで最終的なノイズを低減する
class ComplementaryFilter {
  constructor() {
    // 相補フィルター係数: ジャイロの信頼度 (0.0〜1.0)
    // 高い値ほどジャイロを信頼し、遠心力・振動の影響を受けにくい
    // 0.98: 急激な動作時の動的加速度ノイズ（スパイク）を抑制
    this.alpha = 0.98;
    // EMAスムージング係数: 新しい値の重み (0.0〜1.0)
    // 低い値ほどスムーズだが応答が遅れる
    // 0.15: スパイク通過を抑制しつつ応答性を維持
    this.smoothAlpha = 0.15;

    // 姿勢角の状態 [度]
    this.pitch = 0.0;
    this.roll = 0.0;
    this.yaw = 0.0;

    // EMAスムージング用の前回出力値
    this.smoothPitch = 0.0;
    this.smoothRoll = 0.0;

    // 初回フラグ
    this.initialized = false;
  }

  // ax,ay,az: 加速度 [m/s²]
  // gx,gy,gz: ジャイロ [deg/s]
  // mx,my,mz: 地磁気 [μT] (現在は未使用、将来のyaw補正用に予約)
  // dt: サンプル周期 [s]
  update(ax, ay, az, gx, gy, gz, mx, my, mz, dt) {
    // dt が異常な場合はスキップ
    if (dt <= 0 || dt > 2.0) {
      return { pitch: this.smoothPitch, roll: this.smoothRoll, yaw: this.yaw };
    }

    // 加速度から静的姿勢角を計算 [度]
    // センサー軸定義: X=前方(roll軸), Y=左方(pitch軸), Z=上方(yaw軸)
    const accelPitch = Math.atan2(-ax, Math.sqrt(ay * ay + az * az)) * 180 / Math.PI;
    const accelRoll = Math.atan2(ay, az) * 180 / Math.PI;

    if (!this.initialized) {
      // 初回は加速度ベースの値で初期化
      this.pitch = accelPitch;
      this.roll = accelRoll;
      this.smoothPitch = accelPitch;
      this.smoothRoll = accelRoll;
      this.initialized = true;
    }

    // ジャイロ積分で短期変化を追跡
    const gyroPitch = this.pitch + gy * dt;
    const gyroRoll = this.roll + gx * dt;

    // 相補フィルター: α でジャイロ(高周波)と加速度(低周波)を融合
    this.pitch = this.alpha * gyroPitch + (1 - this.alpha) * accelPitch;
    this.roll = this.alpha * gyroRoll + (1 - this.alpha) * accelRoll;

    // Yaw はジャイロ積分のみ (加速度では計算不可)
    this.yaw += gz * dt;

    // EMAローパスフィルタで最終スムージング
    this.smoothPitch = this.smoothAlpha * this.pitch + (1 - this.smoothAlpha) * this.smoothPitch;
    this.smoothRoll = this.smoothAlpha * this.roll + (1 - this.smoothAlpha) * this.smoothRoll;

    return {
      pitch: parseFloat(this.smoothPitch.toFixed(2)),
      roll: parseFloat(this.smoothRoll.toFixed(2)),
      yaw: parseFloat(this.yaw.toFixed(2))
    };
  }
}

const dispItems = [
  { "id": 0, "name": "SPEED", "value": 0, "dataKeyStb": "spd_s", "dataKeyPt": "spd_p", "unit": "km/h" },
  { "id": 1, "name": "HEAD", "value": 0, "dataKeyStb": "hed_s", "dataKeyPt": "hed_p", "unit": "deg" },
  { "id": 2, "name": "PITCH", "value": 0, "dataKeyStb": "pit_s", "dataKeyPt": "pit_p", "unit": "deg" },
  { "id": 3, "name": "ROLL", "value": 0, "dataKeyStb": "rol_s", "dataKeyPt": "rol_p", "unit": "deg" },
  { "id": 4, "name": "Distance", "value": 0, "dataKeyStb": "dist_s", "dataKeyPt": "dist_p", "unit": "m" },
  { "id": 5, "name": "Time", "value": 0, "dataKeyStb": "dur_s", "dataKeyPt": "dur_p", "unit": "s" },
  { "id": 6, "name": "Drift", "value": 0, "dataKeyStb": "drf_s", "dataKeyPt": "drf_p", "unit": "deg" },
  { "id": 7, "name": "ax", "value": 0, "dataKeyStb": "ax_s", "dataKeyPt": "ax_p", "unit": "G" },
  { "id": 8, "name": "ay", "value": 0, "dataKeyStb": "ay_s", "dataKeyPt": "ay_p", "unit": "G" },
  { "id": 9, "name": "gz", "value": 0, "dataKeyStb": "gz_s", "dataKeyPt": "gz_p", "unit": "deg/s" },
  { "id": 10, "name": "Az", "value": 0, "dataKeyStb": "az_s", "dataKeyPt": "az_p", "unit": "G" },
  { "id": 11, "name": "gx", "value": 0, "dataKeyStb": "gx_s", "dataKeyPt": "gx_p", "unit": "deg/s" },
  { "id": 12, "name": "gy", "value": 0, "dataKeyStb": "gy_s", "dataKeyPt": "gy_p", "unit": "deg/s" },
  { "id": 13, "name": "Dir", "value": 0, "dataKeyStb": "dir_s", "dataKeyPt": "dir_p", "unit": "deg" },
];

const dataLog = [];
const gpsLog = [];
const motionLog = [];
var gpsCounter = 0;
var motionCounter = 0;

function Device(id, spd, vmg, ang, pos, rotate, lines, main, wind, wds, wdd, name, maxSpeedPoint) {
  this.id = id;
  this.spd = spd;
  this.vmg = vmg;
  this.ang = ang;
  this.pos = pos;
  this.rotate = rotate;
  this.lines = lines;
  this.main = main;
  this.wind = wind;
  this.windspd = wds;
  this.winddir = wdd;
  this.isStarboard = true;
  this.name = name;
  this.maxSpeedPoint = maxSpeedPoint;
}

class RealtimeAutoRace extends Component {
  ws = null;
  firstGps = true;

  static propTypes = {
    user: PropTypes.object,
    targetPlayer: PropTypes.any,
    handleSelectPlayer: PropTypes.func
  };

  constructor(props) {
    super(props);
    let latlng = this.props.user.organization.location.split(',');
    this.state = {
      list: dispItems,
      device: null,
      selected_device: null,
      graph: dataLog,
      limit: 600,
      isToggleOn: false,
      dataKeyId: 0,
      dataKeyStb: "spd_s",
      dataKeyPt: "spd_p",
      center: { lat: latlng[0], lng: latlng[1] },
      maxLimit: 150, // オートレース向けに上限速度スケールを上げる
      deviceMap: {
        7777: {
          id: '7777',
          spd: 0,
          vmg: 0,
          ang: 0,
          pos: [0, 0],
          rotate: 0,
          lines: [],
        }
      },
      isStarboard: true,
      maxSpeed: 0,
      maxSpeedPoint: null,
      distance: 0,
      time: 0,
      first_gps: null,
      showAllIMU: false,
    };

    this.driftYaw = 0;
    this.yawIdleCount = 0;
    this.yawIdleTime = 0;
    this.gzBuf = [];
    this.lastMotionTime = null;
    this.prevGzSign = 0;  // gzの前フレーム符号 (0=未初期化)

    this.initDir = null;   // スタート時のGPS方位 (基準)
    this.prevDir = null;   // 前フレームのGPS方位
    this.totalDir = 0;     // 左回り累積角度

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleChangeMaxLimit = this.handleChangeMaxLimit.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleUserSelect = this.handleUserSelect.bind(this);
    this.toggleIMU = this.toggleIMU.bind(this);
    this.resetDrift = this.resetDrift.bind(this);
    this.handleReset = this.handleReset.bind(this);
    this.filter = new ComplementaryFilter();
  }

  toggleIMU() {
    this.setState({ showAllIMU: !this.state.showAllIMU });
  }

  // Drift値を手動でリセットする
  resetDrift() {
    this.driftYaw = 0;
    this.yawIdleTime = 0;
    this.gzBuf = [];
    // 表示中のグラフデータのdrift値もリセット
    let graph = this.state.list;
    graph[6].value = 0;
    this.setState({ list: graph });
  }

  // 全データをリセットする
  handleReset(event) {
    // ログ配列とカウンタのクリア
    dataLog.length = 0;
    gpsLog.length = 0;
    motionLog.length = 0;
    gpsCounter = 0;
    motionCounter = 0;

    // 内部変数のクリア
    this.firstGps = true;
    this.driftYaw = 0;
    this.yawIdleTime = 0;
    this.gzBuf = [];
    this.lastMotionTime = null;
    this.prevGzSign = 0;
    this.initDir = null;
    this.prevDir = null;
    this.totalDir = 0;
    this.filter = new ComplementaryFilter();

    // 表示リストの初期化
    const resetList = dispItems.map(item => ({ ...item, value: 0 }));

    this.setState({
      graph: [],
      maxSpeed: 0,
      maxSpeedPoint: null,
      distance: 0,
      time: 0,
      first_gps: null,
      list: resetList
    });
  }

  componentDidMount() {
    let newDevMap = this.state.deviceMap;
    delete newDevMap[7777];
    this.props.getDevices();
  }

  handleUserSelect(option) {
    const device = _.find(this.props.devices, (device) => device.id == option.value);
    this.setState({
      device: device.id,
      selected_device: device
    });
  }

  value() {
    if (this.state.selected_device) {
      return { label: this.state.selected_device.user.last_name + ":" + this.state.device, value: this.state.device };
    } else {
      return { label: 'NoUser', value: 'NoUser' };
    }
  }

  options() {
    if (this.props.devices) {
      let options = this.props.devices.map((device) => { return { label: device.user.last_name + ":" + device.id, value: device.id }; });
      return options;
    }
  }

  handleInputChange(event) {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    this.setState({
      [name]: value
    });
  }

  handleChangeMaxLimit(event) {
    this.setState({ maxLimit: event.target.value });
  }

  handleSubmit(event) {
    if (this.state.isToggleOn) {
      this.ws.close();
      return;
    }

    if (!this.state.device) {
      alert("Please Select User!");
      return;
    }

    this.firstGps = true;
    this.setState({
      distance: 0,
      time: 0,
      first_gps: null,
      maxSpeed: 0,
      maxSpeedPoint: null,
    });

    this.ws = new WebSocket(wsUrl + this.props.user.organization.id);

    this.ws.onopen = () => {
      console.log('connected');
      this.setState({ isToggleOn: true });
    };

    this.ws.onmessage = evt => {
      let result = JSON.parse(evt.data);
      let graph = this.state.list;
      console.log(result);

      const now = new Date();

      // Motion
      if ((result.type === "motion") && (result.id == String(this.state.device))) {
        const ax = result.d1 || 0;
        const ay = result.d2 || 0;
        const az = result.d3 || 0;
        const gx = result.d4 || 0;
        const gy = result.d5 || 0;
        const gz = result.d6 || 0;
        const mx = result.d7 || 0;
        const my = result.d8 || 0;
        const mz = result.d9 || 0;

        // DTの算出 (実測秒数差分、初回は1.0秒)
        let DT = 1.0;
        if (this.lastMotionTime) {
          DT = (now.getTime() - this.lastMotionTime.getTime()) / 1000.0;
        }
        this.lastMotionTime = now;

        // クライアント側姿勢推定 (相補フィルター + EMAスムージング)
        const est = this.filter.update(ax, ay, az, gx, gy, gz, mx, my, mz, DT);
        result.pitch = -est.pitch;  // AutoRace: センサー取り付け方向によりpitchの符号を反転
        result.roll = est.roll;
        result.yaw = est.yaw;

        const axG = ax / 9.80665;
        const ayG = ay / 9.80665;

        // Drift角（ジャイロ gz の積分）の計算
        // 固定ウィンドウ: 25Hz × 2秒 = 50サンプル
        const DRIFT_WINDOW = 50;
        this.gzBuf.push(gz);
        if (this.gzBuf.length > DRIFT_WINDOW) this.gzBuf.shift();

        let avgChange = 0;
        if (this.gzBuf.length >= DRIFT_WINDOW) {
          // 2秒間の|gz|の平均値 (deg/s)
          avgChange = this.gzBuf.reduce((s, v) => s + Math.abs(v), 0) / DRIFT_WINDOW;
        }

        // 2秒間の平均gz（角速度）が1.0 deg/s未満の場合は静止とみなしリセット
        if (this.gzBuf.length >= DRIFT_WINDOW && avgChange < 1.0) {
          this.driftYaw = 0;
          this.prevGzSign = 0;
        } else {
          // gz符号反転の検出（|gz| >= 1.0 のフレームのみ判定、ノイズ回避）
          if (Math.abs(gz) >= 1.0) {
            const currentGzSign = gz > 0 ? 1 : -1;
            if (this.prevGzSign !== 0 && currentGzSign !== this.prevGzSign) {
              // 符号が反転したらDriftをリセット
              this.driftYaw = 0;
            }
            this.prevGzSign = currentGzSign;
          }
          this.driftYaw += gz * DT;
        }

        const driftVal = parseFloat(this.driftYaw.toFixed(2));

        const pitchVal = (result.pitch !== undefined && result.pitch !== null) ? result.pitch : 0;
        const rollVal = (result.roll !== undefined && result.roll !== null) ? result.roll : 0;

        const gzVal = result.d6 !== undefined && result.d6 !== null ? result.d6 : 0;
        const azG = (result.d3 !== undefined && result.d3 !== null ? result.d3 : 0) / 9.80665;
        const gxVal = result.d4 !== undefined && result.d4 !== null ? result.d4 : 0;
        const gyVal = result.d5 !== undefined && result.d5 !== null ? result.d5 : 0;

        graph[2].value = Number(pitchVal).toFixed(1);
        graph[3].value = Number(rollVal).toFixed(1);
        graph[6].value = driftVal;
        graph[7].value = axG.toFixed(2);
        graph[8].value = ayG.toFixed(2);
        graph[9].value = Number(gzVal).toFixed(2);
        graph[10].value = azG.toFixed(2);
        graph[11].value = Number(gxVal).toFixed(2);
        graph[12].value = Number(gyVal).toFixed(2);

        let m = {
          time: now,
          pit_s: graph[2].value,
          rol_s: graph[3].value,
          drf_s: driftVal,
          ax_s: axG,
          ay_s: ayG,
          gz_s: gzVal,
          az_s: azG,
          gx_s: gxVal,
          gy_s: gyVal,
          pit_p: null,
          rol_p: null,
          drf_p: null,
          ax_p: null,
          ay_p: null,
          gz_p: null,
          az_p: null,
          gx_p: null,
          gy_p: null,
        };

        motionCounter = motionCounter + 1;
        updateGraphData(motionLog, m, motionCounter, this.state.limit);
      }

      // GPS
      if ((result.type === "gps")) {
        let current_device_id = String(this.state.device);
        let main = (result.id === current_device_id);

        let name = "";
        if (this.props.devices) {
          this.props.devices.map((device) => {
            if (device.id == result.id) {
              name = device.user.last_name;
            }
          });
        }

        let newDevMap = this.state.deviceMap;
        let last_gps = newDevMap[result.id];

        let to_unit = 1.0;
        if (this.props.user.unit == "knot") to_unit = 1.852;
        let spd_val = (result.spd / to_unit).toFixed(2);
        let hed_val = result.dir.toFixed(0);

        if (newDevMap.hasOwnProperty(result.id)) {
          let lines = newDevMap[result.id].lines;
          lines.push({ pos: [result.lat, result.long], isStb: true });

          let currentSpeed = parseFloat(spd_val);
          let maxSpeedPoint = newDevMap[result.id].maxSpeedPoint;

          if (!maxSpeedPoint || currentSpeed > parseFloat(maxSpeedPoint.speed)) {
            maxSpeedPoint = {
              speed: currentSpeed,
              pos: [result.lat, result.long],
              time: now,
              isStb: true
            };
            if (main) {
              this.setState({ maxSpeed: currentSpeed, maxSpeedPoint: maxSpeedPoint });
            }
          }

          let dev = new Device(result.id, spd_val, 0, 0,
            { lat: result.lat, lng: result.long }, hed_val, lines,
            newDevMap[result.id].main, false, 0, 0, name, maxSpeedPoint);
          delete newDevMap[result.id];
          newDevMap[result.id] = dev;

          if (main) {
            if (this.firstGps && Math.abs(result.lat) > 0) {
              this.setState({
                center: { lat: result.lat, lng: result.long },
                first_gps: result,
              });
              this.firstGps = false;
              this.initDir = parseFloat(result.dir); // 基準方位を記録
              this.prevDir = this.initDir;
              this.totalDir = 0;
              return;
            }

            let distance = calc_distance(result.lat, result.long, last_gps.pos.lat, last_gps.pos.lng);
            let total_distance = this.state.distance + distance;

            let t0 = this.state.first_gps.time;
            let duration = (convertGpsTime(result.time) - convertGpsTime(t0)) / 1000;

            // Dir：左回り累積角度の計算
            // 現在方位 - 前方位 の差分を算出し、-180～+180に正規化
            // 左回り(反時計)を正にするため符号を反転
            let dirVal = 0;
            if (this.initDir !== null && this.prevDir !== null) {
              const currentDir = parseFloat(result.dir);
              let delta = this.prevDir - currentDir; // 左回りの場合プラス
              // -180～+180に正規化
              if (delta > 180) delta -= 360;
              if (delta < -180) delta += 360;
              this.totalDir += delta;
              this.prevDir = currentDir;
              dirVal = parseFloat(this.totalDir.toFixed(1));
            }
            graph[13].value = dirVal;

            this.setState({
              distance: total_distance,
              time: duration,
            });

            graph[0].value = spd_val;
            graph[1].value = hed_val;
            graph[4].value = total_distance.toFixed(1);
            graph[5].value = duration.toFixed(0);

            let g = {
              time: now,
              spd_s: spd_val,
              hed_s: hed_val,
              dist_s: total_distance,
              dur_s: duration,
              dir_s: graph[13].value,
              spd_p: null,
              hed_p: null,
              dist_p: null,
              dur_p: null,
              dir_p: null,
            };

            gpsCounter = gpsCounter + 1;
            updateGraphData(gpsLog, g, gpsCounter, this.state.limit);
          }
        } else {
          let dev = new Device(result.id, spd_val, 0, 0,
            { lat: result.lat, lng: result.long }, hed_val, [], main, false, 0, 0, "wind", null);
          newDevMap[result.id] = dev;
        }

        this.setState({ deviceMap: newDevMap });
      }

      if (this.state.dataKeyId <= 1) {
        this.ary = gpsLog;
      } else if (this.state.dataKeyId <= 3) {
        this.ary = motionLog;
      } else if (this.state.dataKeyId === 4 || this.state.dataKeyId === 5) {
        this.ary = gpsLog;
      } else if (this.state.dataKeyId === 13) {
        this.ary = gpsLog;
      } else {
        this.ary = motionLog;
      }

      this.setState({
        list: graph.slice(),
        graph: this.ary.slice()
      });
    };

    this.ws.onclose = () => {
      console.log('disconnected');
      this.setState({ isToggleOn: false });
    };
  }

  changeGraph(id) {
    let disp = this.state.list;
    this.setState({
      dataKeyId: id,
      dataKeyStb: disp[id].dataKeyStb,
      dataKeyPt: disp[id].dataKeyPt
    });
  }

  render() {
    return (
      <Container>
        <Row>
          <Col xs={6}>
            <Dropdown
              options={this.options()}
              value={this.value()}
              onChange={this.handleUserSelect}
            />
          </Col>
          <Col xs={6}>
            <Button variant="outline-primary" onClick={this.handleSubmit}> {this.state.isToggleOn ? 'Connected!' : 'Connect'}</Button>
            Limit:<input type="text" name="limit" value={this.state.limit} onChange={this.handleInputChange} />
          </Col>
        </Row>
        <Row>
          <Col>
            <MapBrowser
              deviceMap={this.state.deviceMap}
              deviceId={String(this.state.device)}
              center={this.state.center}
              windSpeed={0} windDirection={0}
              windpos={{ lat: 0, lng: 0 }}
              user={this.props.user}
            />
          </Col>
          <Col xs={7}>
            <Table striped bordered hover variant="dark">
              <tbody className="GridData">
                <tr>
                  <td className={this.state.dataKeyId === 0 ? 'Active' : 'NotActive'} onClick={this.changeGraph.bind(this, 0)}>SPEED {this.state.list[0].value} <span className="Unit">{this.props.user.unit}</span></td>
                  <td className={this.state.dataKeyId === 1 ? 'Active' : 'NotActive'} onClick={this.changeGraph.bind(this, 1)}>Heading {this.state.list[1].value} <span className="Unit">{this.state.list[1].unit}</span></td>
                </tr>
                <tr>
                  <td className={this.state.dataKeyId === 2 ? 'Active' : 'NotActive'} onClick={this.changeGraph.bind(this, 2)}>PITCH {this.state.list[2].value} <span className="Unit">{this.state.list[2].unit}</span></td>
                  <td className={this.state.dataKeyId === 3 ? 'Active' : 'NotActive'} onClick={this.changeGraph.bind(this, 3)}>ROLL  {this.state.list[3].value} <span className="Unit">{this.state.list[3].unit}</span></td>
                </tr>
                <tr>
                  <td className={this.state.dataKeyId === 4 ? 'Active' : 'NotActive'} onClick={this.changeGraph.bind(this, 4)}>Log {this.state.list[4].value} <span className="Unit">{this.state.list[4].unit}</span></td>
                  <td className={this.state.dataKeyId === 6 ? 'Active' : 'NotActive'} onClick={this.changeGraph.bind(this, 6)}>Drift {this.state.list[6].value} <span className="Unit">{this.state.list[6].unit}</span></td>
                </tr>
                <tr>
                  <td className={this.state.dataKeyId === 7 ? 'Active' : 'NotActive'} onClick={this.changeGraph.bind(this, 7)}>ax {this.state.list[7].value} <span className="Unit">{this.state.list[7].unit}</span></td>
                  <td className={this.state.dataKeyId === 8 ? 'Active' : 'NotActive'} onClick={this.changeGraph.bind(this, 8)}>ay {this.state.list[8].value} <span className="Unit">{this.state.list[8].unit}</span></td>
                </tr>
                <tr>
                  <td className={this.state.dataKeyId === 13 ? 'Active' : 'NotActive'} onClick={this.changeGraph.bind(this, 13)}>Dir {this.state.list[13].value} <span className="Unit">{this.state.list[13].unit}</span></td>
                  <td className={this.state.dataKeyId === 9 ? 'Active' : 'NotActive'} onClick={this.changeGraph.bind(this, 9)}>gz {this.state.list[9].value} <span className="Unit">{this.state.list[9].unit}</span></td>
                </tr>
                <tr>
                  <td colSpan="2" style={{ textAlign: 'center', cursor: 'pointer', fontWeight: 'bold', color: '#17a2b8' }} onClick={this.toggleIMU}>
                    {this.state.showAllIMU ? "全データを閉じる" : "全データ表示"}
                  </td>
                </tr>
                {this.state.showAllIMU && (
                  <>
                    <tr>
                      <td className={this.state.dataKeyId === 10 ? 'Active' : 'NotActive'} onClick={this.changeGraph.bind(this, 10)}>Az {this.state.list[10].value} <span className="Unit">{this.state.list[10].unit}</span></td>
                      <td className={this.state.dataKeyId === 11 ? 'Active' : 'NotActive'} onClick={this.changeGraph.bind(this, 11)}>gx {this.state.list[11].value} <span className="Unit">{this.state.list[11].unit}</span></td>
                    </tr>
                    <tr>
                      <td className={this.state.dataKeyId === 12 ? 'Active' : 'NotActive'} onClick={this.changeGraph.bind(this, 12)}>gy {this.state.list[12].value} <span className="Unit">{this.state.list[12].unit}</span></td>
                      <td />
                    </tr>
                  </>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
        <Row>
          <Col xs={5}></Col>
          <Col >
            <Button variant="secondary" onClick={this.handleReset}>RESET</Button>
          </Col>
          <Col xs={4}>
          </Col>
          <Col xs={3}>
            <span className={"ManualWind"}>Max Y: <input name={"maxlimit"} className={"WindValue"} type="number" maxLength={3} size={3} pattern="[0-9]*" value={this.state.maxLimit} onChange={this.handleChangeMaxLimit} /></span>
          </Col>
        </Row>
        <Row>
          <Col>
            <ResponsiveContainer width="100%" height="80%" minWidth={400} minHeight={300}>
              <LineChart data={this.state.graph} >
                <XAxis dataKey="time" />
                <YAxis domain={this.state.dataKeyId === 0 ? [0, this.state.maxLimit] : ['auto', 'auto']} />
                <Tooltip />
                <CartesianGrid stroke="#eee" strokeDasharray="5 5" />
                <Line type="monotone" dataKey={this.state.dataKeyStb} stroke="#006400" isAnimationActive={false} />
                {this.state.maxSpeedPoint && <ReferenceDot x={this.state.maxSpeedPoint.time} y={this.state.maxSpeedPoint.speed} r={5} fill="gold" stroke="none" />}
              </LineChart>
            </ResponsiveContainer>
          </Col>
        </Row>
      </Container>
    );
  }
}

function updateGraphData(list, data, counter, limit) {
  if (counter < limit) {
    list.push(data);
  } else {
    list.shift();
    list.push(data);
  }
}

const mapStateToProps = state => {
  return {
    user: state.auth.user,
    targetUser: state.PlayerSelector.user,
    devices: state.PlayerSelector.devices,
  }
}

const mapDispatchToProps = dispatch => {
  return {
    getDevices: () => dispatch(playerSelector.getDevices()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(RealtimeAutoRace);
